import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// Types matching Django backend exactly
export interface FamilyIllnessRecord {
  dependant: number;
  disease: string;
}

export interface HealthAssessmentPrefill {
  type: 'self' | 'dependant';
  id: number;
  name: string;
  gender?: 'M' | 'F' | 'O';
  dob?: string;
  age?: number;
  relationship?: string;
}

export interface HealthAssessment {
  id: number;
  for_whom: 'self' | 'dependant';
  dependant?: number;
  dependant_data?: {
    id: number;
    name: string;
    relationship?: string;
  };
  status: 'in_process' | 'active' | 'archived';
  current_step: number; // 1–13

  age?: number;
  gender?: 'M' | 'F' | 'O';

  // Step 1: Mood
  mood_today?: number; // 1-5

  // Step 2: Basic Profile
  height_unit?: 'feet' | 'cm';
  height_feet?: number;
  height_inches?: number;
  height_cm?: number;
  weight_kg?: number;
  bmi_value?: number;
  bmi?: string; // 'underweight' | 'normal' | 'overweight' | 'obese'
  health_opinion?: 'extremely_healthy' | 'healthy' | 'unhealthy' | string;

  // Step 3: Presenting Illness
  presenting_illness?: string; // 'cough' | 'cold' | 'fever' | 'diabetes' | 'hypertension' | 'asthma' | 'flu' | 'fine' | 'other'
  presenting_illness_other?: string;

  // Step 4: Past History
  chronic_illness?: boolean;
  chronic_illness_details?: string;
  surgery_history?: boolean;
  surgery_history_details?: string;

  // Step 5: Sleep
  sleep_hours?: string; // 'lt_7' | 'gt_7'
  wakeup_midnight?: boolean;
  wakeup_midnight_reasons?: string;
  tired_morning?: boolean;

  // Step 6: Eating Habits
  junk_food_freq?: string; // 'never' | 'occasionally' | 'often' | 'very_often' | 'always'
  fruits_veg_freq?: string;
  milk_dairy_freq?: string;
  water_intake?: number; // glasses of water per day
  is_veg?: boolean;
  non_veg_freq?: string;

  // Step 7: Alcohol
  alcohol_current?: boolean;
  alcohol_frequency?: string; // 'daily' | 'weekly' | 'occasional'
  alcohol_quantity?: string;
  alcohol_duration?: string; // 'lt_1' | '1_5' | '5_10' | 'gt_10'
  alcohol_planning_quit?: boolean;
  alcohol_past?: boolean;
  alcohol_quit_period?: string; // 'lt_1' | '1_3' | '3_5' | 'gt_5'

  // Step 8: Tobacco
  tobacco_current?: boolean;
  tobacco_type?: string; // 'cigarette' | 'bedi' | 'gutka' | 'raw_tobacco' | 'pan'
  tobacco_duration?: string;
  tobacco_planning_quit?: boolean;
  tobacco_quit?: boolean;
  tobacco_quit_period?: string;

  // Step 9: Hereditary
  family_chronic_illness?: boolean;
  family_illness_records?: FamilyIllnessRecord[];
  checkup_frequency?: string; // 'six_months' | 'yearly' | 'few_times'
  taking_regular_meds?: boolean;
  stopped_meds_without_doctor?: boolean;
  other_alt_medicine?: boolean;

  // Step 10: Bowel/Bladder
  difficulty_urine?: boolean;
  difficulty_urine_reasons?: string;
  difficulty_stools?: boolean;

  // Step 11: Fitness
  stretch_duration?: string; // 'lt_30' | '30_60' | 'gt_60' | 'none'
  cardio_duration?: string;
  strength_duration?: string;
  walking_duration?: string;
  other_activity?: string; // 'none' | 'running' | 'sports' | 'cycling' | 'gymnastics' | 'other'
  other_activity_text?: string;

  // Step 12: Mental Wellness (frequency scale: never/occasionally/often/very_often/always)
  low_interest?: string;
  depressed?: string;
  sleep_appetite_issue?: string;
  low_energy?: string;
  anxious?: string;

  // Step 13: Employee Wellness
  work_stress_affecting_life?: boolean;
  work_stress_reasons?: string[] | string;

  // Results
  report_url?: string;
  risk_category?: string;
  total_score?: number;

  created_at: string;
  updated_at: string;
}

export interface HealthAssessmentListItem {
  id: number;
  for_whom: { type: string; name: string } | string;
  dependant_data?: any;
  status: 'in_process' | 'active' | 'archived';
  current_step: number;
  total_score?: number;
  report_url?: string;
  risk_category?: string;
  created_at: string;
}

export interface CreateHealthAssessmentPayload {
  for_whom: 'self' | 'dependant';
  dependant?: number;
}

export interface HealthAssessmentChoices {
  mood_today: Record<string, string>;
  height_unit: Record<string, string>;
  bmi_category: Record<string, string>;
  health_opinion: Record<string, string>;
  presenting_illness: Record<string, string>;
  eat_frequency: Record<string, string>;
  sleep_hours: Record<string, string>;
  checkup_frequency: Record<string, string>;
  fitness_duration: Record<string, string>;
  other_activity: Record<string, string>;
  risk_category: Record<string, string>;
  wakeup_midnight_reasons: Record<string, string>;
  alcohol_frequency: Record<string, string>;
  alcohol_duration: Record<string, string>;
  alcohol_quit_period: Record<string, string>;
  tobacco_type: Record<string, string>;
  tobacco_duration: Record<string, string>;
  tobacco_quit_period: Record<string, string>;
  family_disease_list: Record<string, string>;
  urine_difficulty_reasons: Record<string, string>;
  work_stress_reasons: Record<string, string>;
}

export interface HealthAssessmentStats {
  total: number;
  completed: number;
  in_progress: number;
  health_areas: number;
}

export type ReportItemStatus = 'good' | 'caution' | 'concern' | 'neutral';
export type ReportPriority = 'concern' | 'caution' | 'good';

export interface ReportSectionItem {
  label: string;
  value: string;
  status: ReportItemStatus;
}

export interface ReportSection {
  title: string;
  status_label: string;
  status_color: string;
  status_bg: string;
  items: ReportSectionItem[];
}

export interface ReportMetric {
  label: string;
  value: string;
  score: number | null;
  color: string;
  bg_color: string;
  icon_letter?: string;
}

export interface ReportRecommendation {
  title: string;
  text: string;
  icon_color: string;
  priority: ReportPriority;
}

export interface ReportData {
  assessment_id: number;
  generated_date: string;

  display_name: string;
  gender_display: string | null;
  patient_age: number | null;
  risk_category: string | null;

  overall_score: number;
  status_label: string;
  status_color: string;
  status_bg: string;
  narrative: string;
  focus_areas: string[];

  component_scores: {
    sleep: number;
    diet: number;
    fitness: number;
    mental: number;
  };

  metrics: ReportMetric[];

  height_display: string;
  weight_display: string;
  bmi_numeric: number | null;
  bmi_color: string;
  bmi_bg: string;
  bmi_label: string;
  health_opinion_display: string;
  health_opinion_color: string;

  sections: ReportSection[];
  recommendations: ReportRecommendation[];
}

export interface HealthAssessmentAnalytics {
  trend: Array<{
    id: number;
    created_at: string;
    score: number;
    risk_category: string;
  }>;
  distribution: { low: number; medium: number; high: number };
  latest_score: {
    id: number;
    score: number;
    risk_category: string;
    previous_score: number | null;
    score_diff: number | null;
    assessments_completed: number;
  } | null;
}

const healthAssessmentService = {
  // Get all assessments (transparently follows DRF pagination so the caller
  // always sees the complete list — fixes stat cards capping at PAGE_SIZE).
  getAssessments: async (): Promise<HealthAssessmentListItem[]> => {
    const all: HealthAssessmentListItem[] = [];
    let page = 1;
    const MAX_PAGES = 50; // safety cap

    while (page <= MAX_PAGES) {
      const response: any = await axiosInstance.get(API_ENDPOINTS.healthAssessment.base, {
        params: page === 1 ? {} : { page },
      });
      const data = response.data;

      if (Array.isArray(data)) {
        all.push(...data);
        break;
      }

      const results: HealthAssessmentListItem[] = data?.results || [];
      all.push(...results);

      if (!data?.next) break;
      page += 1;
    }

    return all;
  },

  // Get single assessment
  getAssessment: async (id: number): Promise<HealthAssessment> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}${id}/`);
    return response.data.data || response.data;
  },

  // Create new assessment
  createAssessment: async (data: CreateHealthAssessmentPayload): Promise<HealthAssessment> => {
    const response = await axiosInstance.post(API_ENDPOINTS.healthAssessment.base, data);
    return response.data.data || response.data;
  },

  // Update assessment (save step) - accepts any partial data
  updateAssessment: async (id: number, data: Record<string, any>): Promise<HealthAssessment> => {
    const response = await axiosInstance.patch(`${API_ENDPOINTS.healthAssessment.base}${id}/`, data);
    return response.data.data || response.data;
  },

  // Delete assessment
  deleteAssessment: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.healthAssessment.base}${id}/`);
  },

  // Submit assessment (finalize). Response also carries `report_data` — the
  // full report payload — so the results page can render without a follow-up
  // /report/ call.
  submitAssessment: async (id: number): Promise<HealthAssessment & { report_data?: ReportData }> => {
    const response = await axiosInstance.post(`${API_ENDPOINTS.healthAssessment.base}${id}/submit/`);
    return response.data;
  },

  // Fetch the shared report payload for a submitted assessment.
  getReport: async (id: number): Promise<ReportData> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}${id}/report/`);
    return response.data;
  },

  // Get prefill data for self or dependant
  getPrefill: async (dependantId?: number): Promise<HealthAssessmentPrefill> => {
    const params = dependantId ? { dependant_id: dependantId } : {};
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}prefill/`, { params });
    return response.data;
  },

  // Download report
  downloadReport: async (id: number): Promise<Blob> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}${id}/download_report/`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get choices for dropdowns
  getChoices: async (): Promise<HealthAssessmentChoices> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}choices/`);
    return response.data;
  },

  getStats: async (): Promise<HealthAssessmentStats> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}stats/`);
    return response.data;
  },

  getAnalytics: async (): Promise<HealthAssessmentAnalytics> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.healthAssessment.base}analytics/`);
    return response.data;
  },
};

export default healthAssessmentService;
